import type { ApiSchemas } from '@rental/contracts/client'
import {
  type FiscalDocumentKind,
  PAYMENT_DOCUMENT_KINDS,
  type PaymentMethod,
  REFUND_DOCUMENT_KINDS,
  type RefundMethod,
  TILL_DOCUMENT_KINDS,
} from '@rental/domain'
import { romeInstant, romeNow } from '../time'

/**
 * S6 (CMP-3, decision 3): a fiscal document as the counter types it from
 * what the till (or someone else) printed. The app issues nothing; the
 * server checks every field again.
 */

export type FiscalMoney = 'payment' | 'refund'
export type DocumentSource = 'till' | 'other'
type StaffFiscalDocument = ApiSchemas['StaffFiscalDocument']

export interface DocumentDraft {
  kind: FiscalDocumentKind
  source: DocumentSource
  number: string
  /** The till's point, when the till printed it and no cash drawer names one. */
  locationId: string | null
  /** As printed, Rome wall-clock; null for now: sent without, the server stamps it. */
  printed: { date: string; time: string } | null
}

/** What the API takes: `FiscalDocumentPrinted`. */
export interface DocumentBody {
  kind: FiscalDocumentKind
  source: DocumentSource
  number: string
  issuedAt?: string
  locationId?: string
}

/** A payment is given a receipt or an invoice; a refund, a return or a credit note. */
export function kindsFor(money: FiscalMoney): readonly FiscalDocumentKind[] {
  return money === 'payment' ? PAYMENT_DOCUMENT_KINDS : REFUND_DOCUMENT_KINDS
}

/** The till prints receipts and returns only: an invoice or a credit note comes from elsewhere. */
export function sourcesFor(kind: FiscalDocumentKind): readonly DocumentSource[] {
  return (TILL_DOCUMENT_KINDS as readonly FiscalDocumentKind[]).includes(kind)
    ? ['till', 'other']
    : ['other']
}

/** What the till most likely printed for this money: a bank transfer is invoiced. */
export function defaultKind(
  money: FiscalMoney,
  method: PaymentMethod | RefundMethod | '',
): FiscalDocumentKind {
  if (method === 'bank_transfer') return money === 'payment' ? 'invoice' : 'credit_note'
  return money === 'payment' ? 'receipt' : 'return'
}

export function newDraft(
  money: FiscalMoney,
  method: PaymentMethod | RefundMethod | '',
  options: { point: string | null; printedAt?: Date },
): DocumentDraft {
  const kind = defaultKind(money, method)
  return {
    kind,
    source: sourcesFor(kind)[0] ?? 'other',
    number: '',
    locationId: options.point,
    printed: options.printedAt ? romeNow(options.printedAt) : null,
  }
}

/** Another kind: the source follows when the old one cannot print it. */
export function withKind(draft: DocumentDraft, kind: FiscalDocumentKind): DocumentDraft {
  const sources = sourcesFor(kind)
  return {
    ...draft,
    kind,
    source: sources.includes(draft.source) ? draft.source : (sources[0] ?? 'other'),
  }
}

export type DocumentOutcome =
  | { ok: true; document: DocumentBody | null }
  | { ok: false; missing: 'point' | 'printed' }

/**
 * What a form sends: nothing when no number is typed (the money then waits
 * in Fiscale), the document otherwise, or what is still missing. Cash names
 * its till through the drawer, so no point is sent with it.
 */
export function documentBody(
  draft: DocumentDraft,
  options: { pointFromDrawer: boolean },
): DocumentOutcome {
  const number = draft.number.trim()
  if (!number) return { ok: true, document: null }
  let issuedAt: string | null = null
  if (draft.printed) {
    issuedAt = romeInstant(draft.printed.date, draft.printed.time)
    if (!issuedAt) return { ok: false, missing: 'printed' }
  }
  const namesPoint = draft.source === 'till' && !options.pointFromDrawer
  if (namesPoint && !draft.locationId) return { ok: false, missing: 'point' }
  return {
    ok: true,
    document: {
      kind: draft.kind,
      source: draft.source,
      number,
      ...(issuedAt ? { issuedAt } : {}),
      ...(namesPoint && draft.locationId ? { locationId: draft.locationId } : {}),
    },
  }
}

/** One payment's or refund's documents: the live one, and the corrected ones, oldest first. */
export function documentsOf(
  documents: readonly StaffFiscalDocument[],
  money: { paymentId: string } | { refundId: string },
): { live: StaffFiscalDocument | null; corrected: StaffFiscalDocument[] } {
  const mine = documents.filter((document) =>
    'paymentId' in money
      ? document.paymentId === money.paymentId
      : document.refundId === money.refundId,
  )
  return {
    live: mine.find((document) => document.supersededAt === null) ?? null,
    corrected: mine.filter((document) => document.supersededAt !== null),
  }
}
